import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../lib/api";
import { toast } from "sonner";
import { ChartBar } from "@phosphor-icons/react";

export default function DigestPanel({ settings, updateTheme, savingTheme, token }) {
  const enabled = !!settings?.digest_enabled;
  const cadence = settings?.digest_cadence || "daily";
  const [sending, setSending] = useState(false);
  const [runs, setRuns] = useState([]);
  const [digestEmail, setDigestEmail] = useState(settings?.digest_email || "");

  useEffect(() => { setDigestEmail(settings?.digest_email || ""); }, [settings?.digest_email]);

  const effectiveRecipient = (settings?.digest_email || settings?.contact_email || "—");

  const saveDigestEmail = (e) => {
    e?.preventDefault();
    const v = digestEmail.trim();
    if (v && !v.includes("@")) { toast.error("Enter a valid email address."); return; }
    updateTheme({ digest_email: v });
    toast.success(v ? `Weekly digest will go to ${v}.` : "Cleared — digest will use your notification email.");
  };

  const loadRuns = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/digest/runs`, { headers: { "X-Admin-Token": token } });
      setRuns(data.runs || []);
    } catch {
      // silent
    }
  };

  useEffect(() => {
    loadRuns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleEnabled = () => updateTheme({ digest_enabled: !enabled });
  const setCadence = (c) => updateTheme({ digest_cadence: c });

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/digest/send?cadence=${cadence}`,
        {},
        { headers: { "X-Admin-Token": token } }
      );
      if (data.ok) {
        toast.success(`Digest sent to ${data.to}.`);
      } else {
        toast.error("Digest send failed — check Brevo SMTP config.");
      }
      loadRuns();
    } catch {
      toast.error("Could not send digest.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex items-start gap-4">
      <ChartBar size={22} color="#FF4500" weight="duotone" />
      <div className="flex-1">
        <h3 className="font-display text-xl">Automated digest email</h3>
        <p className="mt-1 text-[#8E8E93] text-sm">
          Get visitor analytics + new Knowledge AI leads + project submissions delivered to your notification email automatically.
        </p>

        <div className="mt-5 flex items-center gap-3 flex-wrap">
          <button
            onClick={toggleEnabled}
            disabled={savingTheme}
            data-testid="digest-toggle"
            role="switch"
            aria-checked={enabled}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? "bg-[#FF4500]" : "bg-[#2A2A2A]"}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? "translate-x-6" : "translate-x-1"}`} />
          </button>
          <span className={`font-mono text-[10px] tracking-[0.22em] uppercase ${enabled ? "text-[#DFFF00]" : "text-[#8E8E93]"}`}>
            {enabled ? "Auto-send enabled" : "Auto-send paused"}
          </span>

          <div className="ml-auto flex gap-1 border border-[#2A2A2A]" data-testid="digest-cadence">
            {["daily", "weekly"].map((c) => (
              <button
                key={c}
                onClick={() => setCadence(c)}
                disabled={savingTheme}
                data-testid={`digest-cadence-${c}`}
                className={`font-mono text-[10px] tracking-[0.22em] uppercase px-3 py-2 transition-colors ${
                  cadence === c ? "bg-[#FF4500] text-white" : "text-[#8E8E93] hover:text-white"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={saveDigestEmail} className="mt-5 pt-5 border-t border-[#2A2A2A]">
          <label className="ww-label">Send the weekly digest to</label>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-1">
            <input
              type="email"
              value={digestEmail}
              onChange={(e) => setDigestEmail(e.target.value)}
              data-testid="digest-email-input"
              className="ww-input flex-1"
              placeholder={settings?.contact_email || "you@company.com"}
            />
            <button
              type="submit"
              data-testid="digest-email-save"
              disabled={savingTheme || digestEmail.trim() === (settings?.digest_email || "")}
              className="btn-primary disabled:opacity-50"
            >
              {savingTheme ? "Saving…" : "Save recipient"}
            </button>
          </div>
          <div className="mt-2 font-mono text-[10px] text-[#555]">
            Leave blank to use your notification email ({settings?.contact_email || "—"}). Digest currently goes to:{" "}
            <span className="text-[#DFFF00]" data-testid="digest-effective-recipient">{effectiveRecipient}</span>
          </div>
        </form>

        <div className="mt-4 flex items-center gap-3">
          <button
            onClick={sendNow}
            disabled={sending}
            data-testid="digest-send-now"
            className="btn-secondary disabled:opacity-50"
          >
            {sending ? "Sending…" : "Send a test digest now"}
          </button>
          <span className="font-mono text-[10px] text-[#555]">
            Will deliver to: <span className="text-[#DFFF00]">{effectiveRecipient}</span>
          </span>
        </div>

        {runs.length > 0 && (
          <div className="mt-6 pt-5 border-t border-[#2A2A2A]">
            <div className="text-overline mb-2">◢ Recent digest sends ({runs.length})</div>
            <div className="space-y-1.5">
              {runs.slice(0, 5).map((r) => (
                <div key={r.id} className="space-y-1.5" data-testid={`digest-run-${r.id}`}>
                  <div className="flex items-center gap-4 font-mono text-[11px]">
                    <span className="text-[#8E8E93] w-40 shrink-0">{new Date(r.created_at).toLocaleString()}</span>
                    <span className={r.sent_ok ? "text-emerald-400" : "text-[#FF4500]"}>{r.sent_ok ? "✓" : "✗"}</span>
                    <span className="text-white truncate">{r.to_email}</span>
                    <span className="text-[#DFFF00]">{r.window_days === 1 ? "daily" : "weekly"}</span>
                    <span className="text-[#8E8E93] ml-auto">
                      {(r.stats?.page_views_total || 0)} views · {(r.stats?.knowledge_leads || 0)} leads
                    </span>
                  </div>
                  {r.ai_summary && (
                    <div className="pl-[10.5rem] text-[12px] text-[#8E8E93] leading-relaxed italic line-clamp-2" title={r.ai_summary}>
                      &ldquo;{r.ai_summary}&rdquo;
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
